import React, { useState, useEffect } from 'react';
import { Plus, X, Clock, Calendar, Save } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

const DEFAULT_TIMES = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];

const readLocal = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};

const AdminSettings = () => {
  const [availableTimes, setAvailableTimes] = useState(() => readLocal('admin:available-times', DEFAULT_TIMES));
  const [blockedDates, setBlockedDates] = useState(() => readLocal('admin:blocked-dates', []));
  const [newTime, setNewTime] = useState('');
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from('settings').select('key, value').then(({ data }) => {
      (data || []).forEach(r => {
        if (r.key === 'available-times' && Array.isArray(r.value) && r.value.length) setAvailableTimes(r.value);
        if (r.key === 'blocked-dates' && Array.isArray(r.value)) setBlockedDates(r.value);
      });
    });
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    const { error } = await supabase.from('settings').upsert([
      { key: 'available-times', value: availableTimes },
      { key: 'blocked-dates', value: blockedDates }
    ]);
    setSaving(false);
    alert(error ? 'Kunde inte spara: ' + error.message : 'Inställningar sparade! ✅ Gäller nu för alla kunder.');
  };

  const cardStyle = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '32px', marginBottom: '24px' };

  return (
    <div className="max-w-4xl mx-auto" style={{ color: '#1a1a2e' }}>
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Bokningsinställningar</h2>
        <p style={{ color: '#888' }}>Hantera tillgängliga tider och blockerade datum. Tryck Spara längst ner när du är klar.</p>
      </div>

      <div style={cardStyle}>
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2" style={{ color: '#C9A84C' }} />Tillgängliga Tider
        </h3>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-6">
          {availableTimes.map((time) => (
            <div key={time} className="flex items-center justify-between rounded-xl p-3" style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
              <span className="font-semibold text-sm">{time}</span>
              <button onClick={() => setAvailableTimes(availableTimes.filter(t => t !== time))} style={{ color: '#dc2626' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
            className="flex-1 px-4 py-3 rounded-xl focus:outline-none"
            style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }} />
          <button onClick={() => { if (newTime && !availableTimes.includes(newTime)) { setAvailableTimes([...availableTimes, newTime].sort()); setNewTime(''); } }}
            className="px-6 py-3 rounded-xl font-semibold flex items-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
            <Plus className="w-5 h-5" /><span>Lägg Till</span>
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 className="text-xl font-bold mb-2 flex items-center">
          <Calendar className="w-5 h-5 mr-2" style={{ color: '#C9A84C' }} />Blockerade Datum
        </h3>
        <p className="text-sm mb-4" style={{ color: '#888' }}>Kunder kan inte boka dessa datum</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
          {blockedDates.map((date) => (
            <div key={date} className="flex items-center justify-between rounded-xl p-3" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
              <span className="font-semibold text-sm" style={{ color: '#dc2626' }}>{date}</span>
              <button onClick={() => setBlockedDates(blockedDates.filter(d => d !== date))} style={{ color: '#dc2626' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {blockedDates.length === 0 && <p className="text-sm mb-6" style={{ color: '#aaa' }}>Inga blockerade datum</p>}
        <div className="flex gap-2">
          <input type="date" value={newBlockedDate} onChange={(e) => setNewBlockedDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="flex-1 px-4 py-3 rounded-xl focus:outline-none"
            style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }} />
          <button onClick={() => { if (newBlockedDate && !blockedDates.includes(newBlockedDate)) { setBlockedDates([...blockedDates, newBlockedDate].sort()); setNewBlockedDate(''); } }}
            className="px-6 py-3 rounded-xl font-semibold flex items-center gap-2" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
            <Plus className="w-5 h-5" /><span>Blockera</span>
          </button>
        </div>
      </div>

      <button onClick={saveSettings} disabled={saving}
        className="w-full py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2"
        style={{ backgroundColor: '#16a34a', color: '#fff' }}>
        <Save className="w-5 h-5" /><span>{saving ? 'Sparar...' : 'Spara Alla Inställningar'}</span>
      </button>
    </div>
  );
};

export default AdminSettings;
